/**
 * Client-side masking. Runs in the SDK before any event leaves the process.
 * Mirrors apps/collector-api/app/masking.py — keep the two in sync.
 */

export const REDACTED = '***';

export const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'proxy-authorization',
  'x-aris-api-key',
]);

export const SENSITIVE_QUERY_KEYS = new Set([
  'token', 'access_token', 'refresh_token', 'api_key', 'apikey',
  'key', 'password', 'pwd', 'secret', 'sig', 'signature', 'auth',
]);

const JWT = /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{5,}\b/g;
const BEARER = /\bBearer\s+[A-Za-z0-9._-]+/gi;
const LONG_SECRET = /\b[A-Za-z0-9_-]{40,}\b/g;
const EMAIL = /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g;
const PHONE = /\b\+?\d[\d\s().-]{7,}\d\b/g;

export interface MaskOptions {
  maskPii?: boolean;
}

function scrubValue(value: string, opts: MaskOptions): string {
  let v = value.replace(JWT, REDACTED).replace(BEARER, `Bearer ${REDACTED}`).replace(LONG_SECRET, REDACTED);
  if (opts.maskPii) {
    v = v.replace(EMAIL, REDACTED).replace(PHONE, REDACTED);
  }
  return v;
}

export function maskHeaders(
  headers: Record<string, string> | null | undefined,
  opts: MaskOptions = {},
): Record<string, string> | null {
  if (!headers) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    out[k] = SENSITIVE_HEADERS.has(k.toLowerCase()) ? REDACTED : scrubValue(String(v), opts);
  }
  return out;
}

export function maskUrl(url: string, opts: MaskOptions = {}): string {
  try {
    // Use a base so relative URLs parse too.
    const u = new URL(url, 'http://_relative_');
    for (const key of [...u.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        u.searchParams.set(key, REDACTED);
      } else {
        u.searchParams.set(key, scrubValue(u.searchParams.get(key) ?? '', opts));
      }
    }
    const rebuilt = u.host === '_relative_' ? `${u.pathname}${u.search}` : u.toString();
    return scrubValue(rebuilt, opts);
  } catch {
    return scrubValue(url, opts);
  }
}

export function maskText(text: string | null | undefined, opts: MaskOptions = {}): string | null {
  if (!text) return null;
  return scrubValue(text, opts);
}

/** Detect a likely credential in the query string (used to flag token-in-query incidents). */
export function urlHasTokenInQuery(url: string): boolean {
  try {
    const u = new URL(url, 'http://_relative_');
    for (const [k, v] of u.searchParams.entries()) {
      if (SENSITIVE_QUERY_KEYS.has(k.toLowerCase()) && v && v !== REDACTED) return true;
      if (JWT.test(v) || LONG_SECRET.test(v)) return true;
    }
  } catch {
    return false;
  }
  return false;
}
